import React, { FC, useEffect, useState, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { MessageDescriptor, useIntl } from 'react-intl';
import { alpha, makeStyles, Theme, Collapse, Tooltip } from '@material-ui/core';
import { ReactComponent as IconAngleUp } from 'img/icons/angle-up.svg';
import { ReactComponent as IconAngleDown } from 'img/icons/angle-down.svg';
import { SVGIcon } from 'types/common.types';
import { getSidebarContainerVisible, setSidebarContainerVisible } from 'utils/sidebar.utils';
import useSidebarContext from 'common/hooks/context/useSidebarContext';
import SidebarIcon from 'components/Sidebar/SidebarIcon';
import SidebarTooltip from 'components/Sidebar/SidebarTooltip';

const useStyles = makeStyles((theme: Theme) => ({
  collapseContainerActive: {
    '& .sidebarCollapseBtnContent': {
      color: theme.palette.primary.main,
    },
  },
  containerNavLink: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'inherit',
    padding: '10px 14px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.7),
    },
  },
  collapseBtn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'inherit',
    padding: '13px',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.7),
    },
  },
  collapseBtnContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  collapseBtnContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    '& > span': {
      whiteSpace: 'nowrap',
    },
  },
  collapseIcon: {
    width: '14px',
    height: '14px',
    fill: '#8c8c8c',
  },
  collapseContent: {
    '& .MuiCollapse-wrapperInner': {
      display: 'flex',
      flexDirection: 'column',
      '& > a:not(:last-of-type)': {
        marginBottom: '1px',
      },
    },
    '& .sidebarNavLinkText': {
      marginLeft: '32px',
    },
    '& .sidebarGroupTitle': {
      paddingLeft: '24px',
    },
  },
  submenuPopper: {
    marginLeft: '20px',
    overflow: 'hidden',
  },
  submenuTooltip: {
    padding: '8px 0',
    marginLeft: '8.5px',
    marginRight: '0',
    backgroundColor: '#323232',
    color: 'white',
    '& .sidebarGroupTitle': {
      padding: '12px 16px 8px 24px',
    },
    '& .sidebarNavLink': {
      padding: '10px 16px',
      '&:not(:last-child):not(:has(+ .sidebarGroupTitle))': {
        marginBottom: '1px',
      },
    },
    '&:has(.sidebarGroupTitle)': {
      '& .sidebarNavLink': {
        padding: '10px 16px 10px 46px',
      },
    },
    '& > .sidebarTooltipContainer': {
      padding: '0 8px',
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 26px)',
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#323232',
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        marginTop: '-8px',
        marginBottom: '-8px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderColor: '#323232',
        backgroundColor: 'rgba(140, 140, 140, 0.8)',
        '&:hover': {
          backgroundColor: 'rgba(140, 140, 140, 1)',
        },
      },
    },
  },
  submenuArrow: {
    color: '#323232',
    fontSize: '19px',
  },
}));

interface ISidebarContainer {
  id: string;
  title: MessageDescriptor;
  path: string;
  icon: SVGIcon;
  iconViewBox?: string;
  active?: boolean;
  children: (displayNavLinkTitle: boolean) => JSX.Element | Array<JSX.Element | null>;
}

const SidebarContainer: FC<ISidebarContainer> = ({
  id,
  title,
  path,
  icon,
  iconViewBox,
  active = false,
  children,
}) => {
  const [visible, setVisible] = useState<boolean>(getSidebarContainerVisible(id));
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const { sidebarOpen, search } = useSidebarContext();
  const refOpenOnSearch = useRef<boolean>(false);
  const refMouseOverContainer = useRef<boolean>(false);
  const refMouseOverTooltip = useRef<boolean>(false);
  const intl = useIntl();
  const classes = useStyles();
  const translatedTitle = intl.formatMessage(title);
  const tooltip = !sidebarOpen ? children(true) : null;
  let collapseBtn: JSX.Element | null = null;

  const onNavLinkClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();
    refMouseOverContainer.current = false;
    refMouseOverTooltip.current = false;
    setTooltipVisible(false);
  };

  const onContainerMouseEnter = (): void => {
    refMouseOverContainer.current = true;

    setTimeout(() => {
      setTooltipVisible(true);
    }, 100);
  };

  const onContainerMouseLeave = (): void => {
    refMouseOverContainer.current = false;

    setTimeout(() => {
      if (!refMouseOverTooltip.current) {
        setTooltipVisible(false);
      }
    }, 100);
  };

  const onTooltipMouseEnter = (): void => {
    refMouseOverTooltip.current = true;
  };

  const onTooltipMouseLeave = (): void => {
    refMouseOverTooltip.current = false;

    setTimeout(() => {
      if (!refMouseOverContainer.current) {
        setTooltipVisible(false);
      }
    }, 100);
  };

  if (sidebarOpen) {
    const collapseIcon = visible ? (
      <IconAngleUp className={classes.collapseIcon} />
    ) : (
      <IconAngleDown className={classes.collapseIcon} />
    );

    const onContainerClick = (): void => {
      const newVisible = !visible;

      setVisible(newVisible);
      setSidebarContainerVisible(id, newVisible);
    };

    collapseBtn = (
      <button type="button" className={classes.collapseBtn} onClick={onContainerClick}>
        {collapseIcon}
      </button>
    );
  }

  useEffect(() => {
    if (search) {
      if (!visible && !refOpenOnSearch.current) {
        refOpenOnSearch.current = true;
        setVisible(true);
      }
    } else {
      if (refOpenOnSearch.current) {
        refOpenOnSearch.current = false;
      }
    }
  }, [search, visible]);

  return (
    <div
      className={classNames('sidebarContainer', {
        [classes.collapseContainerActive]: active,
      })}
    >
      <div className={classes.collapseBtnContainer}>
        <Tooltip
          arrow
          interactive
          open={tooltipVisible}
          title={tooltip ? <div className="sidebarTooltipContainer">{tooltip}</div> : ''}
          disableFocusListener={sidebarOpen}
          disableHoverListener={sidebarOpen}
          disableTouchListener={sidebarOpen}
          enterDelay={0}
          placement="right"
          classes={{
            popper: classes.submenuPopper,
            tooltip: classes.submenuTooltip,
            arrow: classes.submenuArrow,
          }}
          PopperProps={{
            onMouseEnter: onTooltipMouseEnter,
            onMouseLeave: onTooltipMouseLeave,
            modifiers: {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport',
              },
            },
          }}
        >
          <NavLink
            id={id}
            aria-owns="collapsePopover"
            aria-haspopup="true"
            to={path}
            className={classes.containerNavLink}
            onClick={onNavLinkClick}
            onMouseEnter={!sidebarOpen ? onContainerMouseEnter : undefined}
            onMouseLeave={!sidebarOpen ? onContainerMouseLeave : undefined}
          >
            <div className={classNames('sidebarCollapseBtnContent', classes.collapseBtnContent)}>
              <SidebarIcon icon={icon} iconViewBox={iconViewBox} />
              {sidebarOpen ? <span>{translatedTitle}</span> : null}
            </div>

            {!sidebarOpen ? <SidebarTooltip title={translatedTitle} /> : null}
          </NavLink>
        </Tooltip>

        {collapseBtn}
      </div>

      {sidebarOpen ? (
        <Collapse className={classes.collapseContent} in={visible}>
          {children(sidebarOpen)}
        </Collapse>
      ) : null}
    </div>
  );
};

export default SidebarContainer;
